import { useState } from "react";
import ArrowRight from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ArrowRight24PxOutlined";
import styles from "./CreditOffer.module.scss";
import { Icon } from "./components/Icon/Icon";
import { ListItem } from "../__shared__/ListItem/ListItem";
import { CreditIllustration } from "../../types/cobeTypes";
import { CreditOffersSideDrawer } from "./components/CreditOfferSideDrawer/CreditOffersSideDrawer";

export type CreditOfferProps = {
  /**
   * An array of CreditIllustration objects representing the credit offers.
   */
  offers: CreditIllustration[];
  /**
   * A function that handles item click events, taking a string subtype as an argument.
   */
  ifcDisclaimerAnalyticsClickHandler: (subtype: string) => void;
  /**
   * A function that handles IBC item analytics events, taking a type and a subtype as arguments.
   */
  ibcDisclaimerAnalyticsClickHandler: (type: string, subtype: string) => void;
  /**
   * A boolean that specifies whether or not to hide the component if there are IFC offers.
   */
  hideIfHasIFC?: boolean;
  /**
   * A boolean that specifies whether or not to hide the component if there are are no IFC offers.
   */
  hideIfNoIFC?: boolean;
  /**
   * A boolean that specifies whether or not to show the clear pay message.
   */
  showClearPayMessage?: boolean;
};

export function CreditOfferUI({
  offers,
  ifcDisclaimerAnalyticsClickHandler,
  ibcDisclaimerAnalyticsClickHandler,
  hideIfHasIFC,
  hideIfNoIFC,
  showClearPayMessage,
}: CreditOfferProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const hasIFCOffers = offers.some((obj) => obj.creditRate.value === 0);
  const hasAnyOffers = offers?.some((obj) => obj.creditRate.value >= 0);
  const isExperienceEnabled = showClearPayMessage;
  const isBottomCreditOffer = hideIfHasIFC && !hideIfNoIFC;

  if (isExperienceEnabled) {
    if (!isBottomCreditOffer || !hasAnyOffers) {
      return null;
    }
  } else {
    if (hideIfHasIFC && hasIFCOffers) return null;
    if (hideIfNoIFC && !hasIFCOffers) return null;
  }

  if (!offers.length) {
    return null;
  }
  const [firstOffer] = offers;
  // @ts-ignore
  const isIFC = firstOffer.financialProductType === "IFC";

  const handleOpenDrawer = () => {
    // dispatchMonetateQEvent("standard-pdp-credit-offer-open-drawer");

    if (isIFC && ifcDisclaimerAnalyticsClickHandler) {
      ifcDisclaimerAnalyticsClickHandler("credit component");
    }

    if (!isIFC && ibcDisclaimerAnalyticsClickHandler) {
      ibcDisclaimerAnalyticsClickHandler("fs slide draw", "pdp");
    }

    setIsDrawerOpen(true);
  };

  const labelText = (
    <>
      <span>
        Pay{" "}
        {
          // @ts-ignore
          firstOffer.monthlyRepayment.display
        }{" "}
        per month {isIFC && "interest free"} over{" "}
        {
          // @ts-ignore
          firstOffer.creditTerm.display
        }
        {!isIFC &&
          ` at ${
            // @ts-ignore
            firstOffer.creditRate.display
          } APR`}
      </span>
      {offers.length > 1 ? ". More payment plans available" : undefined}
    </>
  );

  return (
    <div data-testid="credit-offers-component" className={styles.container}>
      <ListItem
        label={labelText}
        trailingIcon={<ArrowRight />}
        showBottomBorder
        showTopBorder
        onClick={handleOpenDrawer}
        className={styles.message}
        // @ts-ignore
        leadingIcon={<Icon creditRate={firstOffer.creditRate} />}
      />
      <CreditOffersSideDrawer
        handleClose={() => setIsDrawerOpen(false)}
        drawerIsOpen={isDrawerOpen}
        creditOffers={offers}
        ifcDisclaimerAnalyticsClickHandler={ifcDisclaimerAnalyticsClickHandler}
        ibcDisclaimerAnalyticsClickHandler={ibcDisclaimerAnalyticsClickHandler}
      />
    </div>
  );
}
